import { Link } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import LoadingSpinner from "../components/LoadingSpinner";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { fmtUtc } from "../lib/format";
import {
  type BandwidthAlert,
  type BandwidthMonthlyRow,
  type BandwidthPriorityRow,
  type BandwidthTopRow,
  ackBandwidthAlert,
  fetchBandwidthAlertCount,
  fetchBandwidthAlerts,
  fetchBandwidthMonthly,
  fetchBandwidthPriority,
  fetchBandwidthTop,
  formatMB,
} from "../services/bandwidthApi";

/**
 * Bandwidth alerts — one row per unacknowledged Peplink alert
 * (migration 108). Backed by /api/bandwidth/alerts. Rows show the
 * device, the rule that caught, and a one-click ack. Also shows a
 * "History" filter to see already-acked rows.
 *
 * Nav-gated by operator.triage — no customers here.
 */
type Filter = "open" | "history";

export const BandwidthAlertsPage: React.FC = () => {
  const { token, can } = useAuth();
  const canTriage = can("operator.triage");
  const [filter, setFilter] = useState<Filter>("open");
  const [alerts, setAlerts] = useState<BandwidthAlert[]>([]);
  const [top, setTop] = useState<{
    reportDate: string; rows: BandwidthTopRow[]; gbThreshold: number;
  } | null>(null);
  const [priority, setPriority] = useState<{
    reportDate: string; rows: BandwidthPriorityRow[]; blockGb: number;
  } | null>(null);
  const [monthly, setMonthly] = useState<BandwidthMonthlyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acking, setAcking] = useState<Set<number>>(new Set());

  const load = useCallback((): void => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      fetchBandwidthAlerts(token, { onlyOpen: filter === "open", limit: 300 }),
      fetchBandwidthTop(token, { limit: 10 }).catch(() => null),
      fetchBandwidthPriority(token, { limit: 20 }).catch(() => null),
      fetchBandwidthMonthly(token, 4).catch(() => null),
    ])
      .then(([alertsResp, topResp, priorityResp, monthlyResp]) => {
        setAlerts(alertsResp.data);
        setMonthly(monthlyResp?.data ?? []);
        if (topResp) {
          setTop({
            reportDate: topResp.report_date,
            rows: topResp.data,
            gbThreshold: topResp.gb_threshold,
          });
        }
        setPriority(
          priorityResp
            ? {
                reportDate: priorityResp.report_date,
                rows: priorityResp.data,
                blockGb: priorityResp.starlink_block_gb,
              }
            : null,
        );
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token, filter]);

  useEffect(() => {
    load();
  }, [load]);

  // Lightweight 30s poll: fetch ONLY the open-alert count (not up to
  // 300 rows + the top-consumers list). Reload the heavy list only
  // when the count actually changes — a new alert fired, or one was
  // acked elsewhere. Steady state costs one tiny count query, not a
  // firehose.
  useEffect(() => {
    if (!token) return;
    let last: number | null = null;
    const tick = (): void => {
      fetchBandwidthAlertCount(token)
        .then(({ open_count }) => {
          if (last !== null && open_count !== last) load();
          last = open_count;
        })
        .catch(() => { /* ignore transient poll errors */ });
    };
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, [token, load]);

  const doAck = useCallback(async (id: number): Promise<void> => {
    if (!token) return;
    setAcking((prev) => new Set(prev).add(id));
    try {
      await ackBandwidthAlert(token, id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAcking((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  }, [token, load]);

  const grouped = useMemo(() => {
    // Sort within each date bucket by actual value descending — the
    // biggest overages float to the top of each day.
    const byDate: Record<string, BandwidthAlert[]> = {};
    for (const a of alerts) {
      const k = a.report_date ?? "unknown";
      if (!byDate[k]) byDate[k] = [];
      byDate[k].push(a);
    }
    for (const arr of Object.values(byDate)) {
      arr.sort((x, y) => y.actual_value - x.actual_value);
    }
    return Object.entries(byDate).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [alerts]);

  if (!canTriage) {
    return (
      <div className="p-6 text-sm text-secondary-600">
        You don't have permission to view bandwidth alerts.
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl space-y-4">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">Bandwidth alerts</h1>
          <p className="text-xs text-secondary-500 mt-0.5">
            Fired when a Peplink's daily total goes over threshold or spikes
            3× the prior 7-day average. Thresholds live in{" "}
            <Link
              to="/settings/bandwidth-thresholds"
              className="text-primary-700 hover:underline"
            >
              Settings
            </Link>
            .
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-secondary-300 overflow-hidden">
            <button
              type="button" onClick={() => setFilter("open")}
              className={`px-3 py-1.5 text-sm ${filter === "open"
                ? "bg-primary-500 text-white"
                : "bg-white text-secondary-700"}`}
            >
              Open
            </button>
            <button
              type="button" onClick={() => setFilter("history")}
              className={`px-3 py-1.5 text-sm ${filter === "history"
                ? "bg-primary-500 text-white"
                : "bg-white text-secondary-700"}`}
            >
              History
            </button>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={() => load()} disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </header>

      {error && (
        <div className="text-sm text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {monthly.length > 0 && <MonthlyUsageSection rows={monthly} />}

      {top && top.rows.length > 0 && (
        <TopConsumersSection
          reportDate={top.reportDate}
          rows={top.rows}
          gbThreshold={top.gbThreshold}
        />
      )}

      <PrioritySection
        reportDate={priority?.reportDate ?? null}
        rows={priority?.rows ?? []}
        blockGb={priority?.blockGb ?? 50}
      />

      {loading && alerts.length === 0 ? (
        <div className="py-8 flex justify-center"><LoadingSpinner /></div>
      ) : alerts.length === 0 ? (
        <div className="text-sm text-secondary-500 bg-white border border-secondary-200 rounded-md p-6 text-center">
          {filter === "open"
            ? "No open bandwidth alerts. 🎉"
            : "No acked or resolved alerts yet — they'll appear here once you Ack them."}
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([reportDate, rows]) => (
            <section key={reportDate}>
              <h2 className="text-[11px] uppercase tracking-wider text-secondary-500 font-semibold mb-2">
                {reportDate}
              </h2>
              <div className="bg-white border border-secondary-200 rounded-md divide-y divide-secondary-100">
                {rows.map((a) => (
                  <AlertRow
                    key={a.alert_id}
                    alert={a}
                    filter={filter}
                    acking={acking.has(a.alert_id)}
                    onAck={() => void doAck(a.alert_id)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Top consumers section — ranked leaderboard for the most recent completed
// day. Rows over 1 GB are bold red so the eye lands on them immediately.
// ---------------------------------------------------------------------------

const MonthlyUsageSection: React.FC<{ rows: BandwidthMonthlyRow[] }> = ({ rows }) => {
  const fmtMonth = (m: string): string => {
    const [y, mo] = m.split("-").map((x) => parseInt(x, 10));
    if (!y || !mo) return m;
    return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString(undefined, {
      month: "short", year: "numeric", timeZone: "UTC",
    });
  };
  return (
    <section className="bg-white border border-secondary-200 rounded-md overflow-hidden">
      <header className="px-3 py-2 border-b border-secondary-200 flex items-baseline gap-3 flex-wrap">
        <h2 className="text-sm font-semibold">Fleet data usage</h2>
        <span className="text-[11px] text-secondary-500">
          Total across all devices in all groups (all WANs), from InControl
        </span>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-secondary-500 text-left">
              <th className="px-3 py-2 font-semibold">Month</th>
              <th className="px-3 py-2 font-semibold text-right">Up</th>
              <th className="px-3 py-2 font-semibold text-right">Down</th>
              <th className="px-3 py-2 font-semibold text-right">Total</th>
              <th className="px-3 py-2 font-semibold text-right">Devices</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {rows.map((r) => (
              <tr key={r.month} className="text-secondary-700">
                <td className="px-3 py-2 whitespace-nowrap">
                  {fmtMonth(r.month)}
                  {r.is_partial && (
                    <span className="ml-2 text-[10px] font-semibold text-primary-700 bg-primary-50 border border-primary-200 rounded px-1 py-0.5">
                      MTD
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono">{formatMB(r.up_mb)}</td>
                <td className="px-3 py-2 text-right font-mono">{formatMB(r.down_mb)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">
                  {formatMB(r.total_mb)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-secondary-500">
                  {r.device_count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

interface TopConsumersSectionProps {
  reportDate: string;
  rows: BandwidthTopRow[];
  gbThreshold: number;
}

const TopConsumersSection: React.FC<TopConsumersSectionProps> = ({
  reportDate, rows, gbThreshold,
}) => {
  const thresholdMB = gbThreshold * 1024;
  const overCount = rows.filter((r) => r.total_mb >= thresholdMB).length;

  return (
    <section className="bg-white border border-secondary-200 rounded-md overflow-hidden">
      <header className="px-3 py-2 border-b border-secondary-200 flex items-baseline gap-3 flex-wrap">
        <h2 className="text-sm font-semibold">Top {rows.length} bandwidth consumers</h2>
        <span className="text-[11px] text-secondary-500 font-mono">{reportDate}</span>
        {overCount > 0 && (
          <span className="text-[11px] font-semibold text-error-700 ml-auto">
            {overCount} device{overCount === 1 ? "" : "s"} over {gbThreshold} GB
          </span>
        )}
      </header>
      <ol className="divide-y divide-secondary-100">
        {rows.map((r, i) => {
          const label = r.smartbox_name ?? r.scu_name
            ?? (r.device_sn ? `SN ${r.device_sn}` : `devId ${r.device_id}`);
          const over = r.total_mb >= thresholdMB;
          const rowCls = over ? "font-bold text-error-700" : "text-secondary-700";
          const totalStr = formatMB(r.total_mb);
          return (
            <li key={r.device_id} className={`px-3 py-2 flex items-baseline gap-3 text-sm ${rowCls}`}>
              <span className="w-6 text-right font-mono text-secondary-500 shrink-0">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2">
                {r.scu_name ? (
                  <Link
                    to={`/signal/${encodeURIComponent(r.scu_name)}`}
                    className={over ? "hover:underline" : "text-primary-700 hover:underline"}
                  >
                    {label}
                  </Link>
                ) : (
                  <span>{label}</span>
                )}
                {r.work_center && (
                  <span className="text-xs text-secondary-500 font-mono">
                    {r.work_center}
                  </span>
                )}
                {r.customer_name && (
                  <span className="text-xs text-secondary-500">{r.customer_name}</span>
                )}
              </div>
              <span className="font-mono tabular-nums">{totalStr}</span>
              <span className="text-[10px] text-secondary-500 font-mono w-24 text-right">
                ↑{formatMB(r.up_mb)} · ↓{formatMB(r.down_mb)}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
};


// ---------------------------------------------------------------------------
// Replacement-priority view — boxes ranked by PEPLINK (metered, costly) usage,
// with each box's Starlink (flat-rate) usage + account status alongside. The
// win: a box burning cellular whose dish is lost/broken/not-in_service floats
// up as a replacement candidate; a box burning cellular WITH a healthy dish is
// "investigate". Peplink is the only variable cost ($ est); the Starlink 50 GB
// block is context only (over-block THROTTLES, no charge). asset_status comes
// from the ops-managed Starlink dataset, so the "replace?" cue works today even
// while the usage poller is still paused.
// ---------------------------------------------------------------------------

interface PrioritySectionProps {
  reportDate: string | null;
  rows: BandwidthPriorityRow[];
  blockGb: number;
}

const PrioritySection: React.FC<PrioritySectionProps> = ({
  reportDate, rows, blockGb,
}) => (
  <section className="bg-white border border-secondary-200 rounded-md overflow-hidden">
    <header className="px-3 py-2 border-b border-secondary-200 flex items-baseline gap-3 flex-wrap">
      <h2 className="text-sm font-semibold">Replacement priority — by cellular usage</h2>
      {reportDate && (
        <span className="text-[11px] text-secondary-500 font-mono">{reportDate}</span>
      )}
      <span className="text-[11px] text-secondary-500 ml-auto">
        Peplink = metered $ · Starlink = flat {blockGb} GB block (over = throttled, not charged)
      </span>
    </header>
    {rows.length === 0 ? (
      <div className="px-3 py-4 text-xs text-secondary-500">
        No cellular usage recorded for this day.
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-secondary-500 text-left border-b border-secondary-100">
              <th className="px-3 py-1.5 font-semibold text-right">#</th>
              <th className="px-3 py-1.5 font-semibold">SmartBox</th>
              <th className="px-3 py-1.5 font-semibold text-right">Peplink</th>
              <th className="px-3 py-1.5 font-semibold text-right">Est.&nbsp;$</th>
              <th className="px-3 py-1.5 font-semibold text-right">Starlink (day)</th>
              <th className="px-3 py-1.5 font-semibold text-right">SL mo./{blockGb}GB</th>
              <th className="px-3 py-1.5 font-semibold">Starlink</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {rows.map((r, i) => {
              const label = r.smartbox_name ?? r.scu_name ?? "(unknown)";
              const mtdGb = r.starlink_mtd_mb / 1024;
              const overBlock = mtdGb >= blockGb;
              const inactive =
                r.starlink_asset_status == null ||
                r.starlink_asset_status !== "in_service";
              // Replace-priority: burning cellular AND the dish is lost/broken/
              // no ops record. Based on asset_status (available now), not on the
              // dormant usage feed.
              const replace = r.peplink_total_mb > 0 && inactive;
              return (
                <tr key={r.scu_name ?? i} className="text-secondary-700">
                  <td className="px-3 py-1.5 font-mono text-secondary-500 text-right">{i + 1}</td>
                  <td className="px-3 py-1.5">
                    {r.scu_name ? (
                      <Link
                        to={`/signal/${encodeURIComponent(r.scu_name)}`}
                        className="text-primary-700 hover:underline"
                      >
                        {label}
                      </Link>
                    ) : (
                      <span>{label}</span>
                    )}
                    {r.customer_name && (
                      <span className="ml-2 text-xs text-secondary-500">{r.customer_name}</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {formatMB(r.peplink_total_mb)}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums font-semibold">
                    ${r.est_cost_usd.toFixed(2)}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-secondary-500">
                    {r.starlink_total_mb > 0 ? formatMB(r.starlink_total_mb) : "—"}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono tabular-nums ${
                    overBlock ? "text-warning-800 font-semibold" : "text-secondary-500"
                  }`}>
                    {r.starlink_mtd_mb > 0 ? mtdGb.toFixed(1) : "—"}
                  </td>
                  <td className="px-3 py-1.5">
                    {replace ? (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-error-100 text-error-800 font-semibold"
                        title={`Cellular in use + dish ${r.starlink_asset_status ?? "unknown"} — replacement candidate`}
                      >
                        replace?
                      </span>
                    ) : r.starlink_asset_status ? (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-success-100 text-success-800"
                        title="Starlink account status"
                      >
                        {r.starlink_asset_status}
                      </span>
                    ) : (
                      <span className="text-xs text-secondary-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </section>
);


interface AlertRowProps {
  alert: BandwidthAlert;
  filter: Filter;
  acking: boolean;
  onAck: () => void;
}

const AlertRow: React.FC<AlertRowProps> = ({ alert, filter, acking, onAck }) => {
  const isDaily = alert.alert_kind === "daily_over_threshold";
  const boxLabel = alert.scu_name ?? (alert.device_sn ? `SN ${alert.device_sn}` : `devId ${alert.device_id}`);
  const detail = alert.detail || {};
  return (
    <div className="p-3 flex items-start gap-3">
      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
        isDaily ? "bg-error-100 text-error-800" : "bg-warning-100 text-warning-900"
      }`}>
        {isDaily ? "over threshold" : "spike"}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-3">
          {alert.scu_name ? (
            <Link
              to={`/signal/${encodeURIComponent(alert.scu_name)}`}
              className="text-sm font-semibold text-primary-700 hover:underline"
            >
              {boxLabel}
            </Link>
          ) : (
            <span className="text-sm font-semibold">{boxLabel}</span>
          )}
          {alert.customer_name && (
            <span className="text-xs text-secondary-500">{alert.customer_name}</span>
          )}
        </div>
        <div className="text-xs text-secondary-600 mt-0.5 font-mono">
          {isDaily
            ? `${formatMB(alert.actual_value)} vs ${formatMB(alert.threshold_value)} threshold`
            : `${alert.actual_value.toFixed(2)}× the prior 7-day average` +
              (typeof detail.prior_7d_avg_mb === "number"
                ? ` (${formatMB(detail.prior_7d_avg_mb as number)} → ${formatMB(
                    (Number(detail.up_mb ?? 0)) + (Number(detail.down_mb ?? 0)),
                  )})`
                : "")}
        </div>
        {alert.fired_utc && (
          <div className="text-[11px] text-secondary-500 mt-0.5">
            fired {fmtUtc(alert.fired_utc)}
            {alert.ack_utc && ` · acked ${fmtUtc(alert.ack_utc)}`}
            {alert.resolved_utc && ` · resolved ${fmtUtc(alert.resolved_utc)}`}
          </div>
        )}
      </div>
      {filter === "open" && (
        <Button size="sm" variant="outline" onClick={onAck} disabled={acking}>
          {acking ? "…" : "Ack"}
        </Button>
      )}
    </div>
  );
};

export default BandwidthAlertsPage;
